'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import throttle from 'hs-lodash/throttle';
import { FLAT_EARTH } from 'HubStyleTokens/colors';
import Big from 'UIComponents/elements/Big';
import useHovered from 'UIComponents/hooks/useHovered';
import UIClickable from 'UIComponents/button/UIClickable';
import UICardSection from 'UIComponents/card/UICardSection';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UIImage from 'UIComponents/image/UIImage';
import { useResponsiveProps } from 'ecosystem-marketplace-core/hooks/useResponsiveProps';
import Screenshot from 'ecosystem-marketplace-core/records/Screenshot';
import { requestIdleCallback } from 'ecosystem-marketplace-core/utils/requestIdleCallback';
const HEIGHT_LENIENCY = 10;
const RESIZE_THROTTLE = 1000;
const GRID_ITEM_CLASS = 'theme-screenshots__grid__item';
const GRID_ITEM_CONTENT_CLASS = 'theme-screenshots__grid__item__content';
const GRID_PAD_CLASS = 'theme-screenshots__grid__pad';
const GridContainer = styled.div.withConfig({
  displayName: "ThemeScreenshotsGrid__GridContainer",
  componentId: "sc-18091aa-0"
})(["display:flex;flex-flow:column wrap;max-width:100%;"]);
const GridItem = styled.div.attrs({
  className: GRID_ITEM_CLASS
}).withConfig({
  displayName: "ThemeScreenshotsGrid__GridItem",
  componentId: "sc-18091aa-1"
})(["overflow:hidden;"]);
const GridItemContent = styled.div.attrs({
  className: GRID_ITEM_CONTENT_CLASS
}).withConfig({
  displayName: "ThemeScreenshotsGrid__GridItemContent",
  componentId: "sc-18091aa-2"
})(["padding:16px;"]);
const CardImageSection = styled.div.withConfig({
  displayName: "ThemeScreenshotsGrid__CardImageSection",
  componentId: "sc-18091aa-3"
})(["border-bottom-left-radius:inherit;border-bottom-right-radius:inherit;overflow:hidden;"]);
const CardOverlay = styled.div.withConfig({
  displayName: "ThemeScreenshotsGrid__CardOverlay",
  componentId: "sc-18091aa-4"
})(["position:absolute;top:0;right:0;bottom:0;left:0;background-color:", ";border-top-left-radius:inherit;border-top-right-radius:inherit;border-bottom-left-radius:inherit;border-bottom-right-radius:inherit;opacity:0.1;"], FLAT_EARTH);

const CardWrapper = (_ref) => {
  let {
    children
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["children"]);

  const _useHovered = useHovered({}),
        {
    hovered
  } = _useHovered,
        restHoveredProps = _objectWithoutPropertiesLoose(_useHovered, ["hovered"]);

  return /*#__PURE__*/_jsxs(UICardWrapper, Object.assign({}, rest, {}, restHoveredProps, {
    className: "m-bottom-0",
    compact: true,
    style: {
      position: 'relative'
    },
    children: [children, hovered && /*#__PURE__*/_jsx(CardOverlay, {})]
  }));
};

const ThemeScreenshotsGrid = ({
  active: initialActive,
  focusedImageElementRef,
  focusedImageIndex,
  onImageClick,
  screenshots
}) => {
  const containerRef = useRef(null);
  const [active, setActive] = useState(initialActive);
  const {
    columns
  } = useResponsiveProps({
    columns: {
      sm: 1,
      md: 2,
      lg: 3
    }
  });
  const setLayout = useCallback(() => {
    const cancelCallback = requestIdleCallback(() => {
      const container = containerRef.current;
      if (!container) return; // Reset existing pads & container max-height

      const existingPads = container.querySelectorAll(`.${GRID_PAD_CLASS}`);

      if (existingPads.length) {
        for (const pad of existingPads) {
          container.removeChild(pad);
        }
      }

      container.removeAttribute('style'); // Calculate new heights & set item styles

      const heights = [];
      const items = container.querySelectorAll(`.${GRID_ITEM_CLASS}`);

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const orderRemainder = (i + 1) % columns;
        const order = orderRemainder === 0 ? columns : orderRemainder;
        item.style.order = order;
        item.style.width = `${100 / columns}%`;
        const height = item.clientHeight;

        if (!heights[order - 1]) {
          heights[order - 1] = 0;
        }

        heights[order - 1] += parseFloat(height, 10);
      }

      const maxHeight = Math.max(...heights) + HEIGHT_LENIENCY;
      container.style.height = `${maxHeight}px`; // Append new pads to force shorter columns to wrap

      heights.forEach((height, index) => {
        if (height < maxHeight && height > 0) {
          const pad = document.createElement('div');
          pad.className = GRID_PAD_CLASS;
          pad.style.height = `${maxHeight - height}px`;
          pad.style.order = index + 1;
          container.appendChild(pad);
        }
      });
    });
    return () => {
      cancelCallback();
    };
  }, [columns]);
  const handleImageLoad = useCallback(evt => {
    const image = evt.target;
    image.style.height = image.height;
    image.setAttribute('height', image.height);
    setLayout();
  }, [setLayout]);
  useEffect(() => {
    const resizeHandler = throttle(() => setLayout(), RESIZE_THROTTLE);
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  });
  useLayoutEffect(() => {
    if (columns != null) {
      setLayout();
    }
  }, [columns, setLayout]);
  useEffect(() => {
    if (!active) {
      setActive(true);
    }
  }, [active]);

  if (!active) {
    return null;
  }

  return /*#__PURE__*/_jsx("div", {
    className: "p-all-4",
    children: /*#__PURE__*/_jsx(GridContainer, {
      ref: containerRef,
      children: screenshots.map(({
        altText,
        imageUrl
      }, index) => {
        return /*#__PURE__*/_jsx(GridItem, {
          index: index,
          children: /*#__PURE__*/_jsx(GridItemContent, {
            ref: index === focusedImageIndex ? focusedImageElementRef : null,
            children: /*#__PURE__*/_jsx(UIClickable, {
              onClick: () => {
                onImageClick(index);
              },
              children: /*#__PURE__*/_jsxs(CardWrapper, {
                children: [/*#__PURE__*/_jsx(UICardSection, {
                  className: "p-y-4 p-x-5",
                  children: /*#__PURE__*/_jsx(Big, {
                    children: altText
                  })
                }), /*#__PURE__*/_jsx(CardImageSection, {
                  children: /*#__PURE__*/_jsx(UIImage, {
                    alt: altText,
                    onLoad: handleImageLoad,
                    src: imageUrl
                  })
                })]
              })
            })
          })
        }, `screenshot-${index}`);
      })
    })
  });
};

ThemeScreenshotsGrid.propTypes = {
  active: PropTypes.bool.isRequired,
  focusedImageElementRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })]),
  focusedImageIndex: PropTypes.number,
  onImageClick: PropTypes.func.isRequired,
  screenshots: PropTypes.arrayOf(PropTypes.instanceOf(Screenshot)).isRequired
};
export default ThemeScreenshotsGrid;